import type { LogSet } from "./schemas/logSet.js";
import type { MuscleSlug } from "./bodymap.js";
import type { Side } from "./side.js";

// #533: pain recorded on a set (LogSet.pain 0-10, LogSet.painZone), folded per
// exercise and zone over a window of sets. The answer to «does this exercise,
// at this load, hurt?»: how many sets carried a pain score, how high it went,
// the load of the worst one, and which sides.
//
// Pure. Only sets with a `pain` score count: a zone with no score says where,
// not how much, and absence never reads as «no pain». A set with a score and
// no zone is grouped under the exercise alone (`zone` absent).
//
// ⛔ Health data. The coach read strips `pain`/`painZone` before this runs
// (coachReadProjection.ts), so a cross-user window folds to an empty list.

export type SetPainSummary = {
  slug: string;
  /** Absent when the sets carried a score but no zone. */
  zone?: MuscleSlug;
  /** Sets with a pain score. */
  sets: number;
  maxPain: number;
  /** Mean of the scores, one decimal. */
  meanPain: number;
  /** Load of the set with the highest score (the latest on a tie), when it had one. */
  maxPainWeightKg?: number;
  maxPainAt: string;
  /** Distinct `side` values of the scored sets, when any set had one. */
  sides?: Side[];
};

type PainSet = Pick<
  LogSet,
  "exerciseSlug" | "pain" | "painZone" | "side" | "weight" | "performedAt"
>;

export const summarizeSetPain = (
  sets: readonly PainSet[],
): SetPainSummary[] => {
  const groups = new Map<
    string,
    SetPainSummary & { total: number; sideSet: Set<Side> }
  >();
  for (const s of sets) {
    if (s.pain === undefined) continue;
    const key = `${s.exerciseSlug}#${s.painZone ?? ""}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        slug: s.exerciseSlug,
        ...(s.painZone ? { zone: s.painZone } : {}),
        sets: 0,
        maxPain: s.pain,
        meanPain: 0,
        maxPainAt: s.performedAt,
        total: 0,
        sideSet: new Set(),
      };
      if (s.weight !== undefined) g.maxPainWeightKg = s.weight;
      groups.set(key, g);
    }
    g.sets += 1;
    g.total += s.pain;
    if (s.side) g.sideSet.add(s.side);
    if (
      s.pain > g.maxPain ||
      (s.pain === g.maxPain && s.performedAt > g.maxPainAt)
    ) {
      g.maxPain = s.pain;
      g.maxPainAt = s.performedAt;
      if (s.weight !== undefined) g.maxPainWeightKg = s.weight;
      else delete g.maxPainWeightKg;
    }
  }
  const out: SetPainSummary[] = [];
  for (const { total, sideSet, ...g } of groups.values()) {
    const row: SetPainSummary = {
      ...g,
      meanPain: Math.round((total / g.sets) * 10) / 10,
    };
    if (sideSet.size > 0) row.sides = [...sideSet].sort();
    out.push(row);
  }
  // Worst first, so a short read sees what hurts most.
  return out.sort(
    (a, b) =>
      b.maxPain - a.maxPain ||
      a.slug.localeCompare(b.slug) ||
      (a.zone ?? "").localeCompare(b.zone ?? ""),
  );
};
